import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import axios from 'axios';

const RegionContext = createContext();
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REGIONS = {
  india: {
    key: 'india',
    label: 'India',
    flag: '🇮🇳',
    currency: 'INR',
    currencySymbol: '₹',
    fulfillment: 'Qikink',
    shipping: { label: 'Free shipping above ₹5,000', cost: 500, freeAbove: 5000 },
    delivery: '5-7 business days',
    country: 'India',
  },
  international: {
    key: 'international',
    label: 'International',
    flag: '🌍',
    currency: 'USD',
    currencySymbol: '$',
    fulfillment: 'Prodigi',
    shipping: { label: 'Standard international shipping', cost: 2500, freeAbove: 15000 },
    delivery: '10-15 business days',
    country: '',
  },
};

export const RegionProvider = ({ children }) => {
  const [regionKey, setRegionKey] = useState(() => {
    return localStorage.getItem('user_region') || null;
  });
  const [exchangeRate, setExchangeRate] = useState(83);
  const [showRegionPicker, setShowRegionPicker] = useState(false);

  const region = regionKey ? REGIONS[regionKey] : null;
  const currency = region?.currency || 'INR';

  useEffect(() => {
    axios.get(`${API}/exchange-rate`)
      .then(res => setExchangeRate(res.data.usd_to_inr))
      .catch(() => {});
  }, []);

  // Show picker on first visit
  useEffect(() => {
    if (!regionKey) {
      const timer = setTimeout(() => setShowRegionPicker(true), 800);
      return () => clearTimeout(timer);
    }
  }, [regionKey]);

  const selectRegion = useCallback((key) => {
    setRegionKey(key);
    localStorage.setItem('user_region', key);
    setShowRegionPicker(false);
  }, []);

  const toggleRegion = useCallback(() => {
    const newKey = regionKey === 'india' ? 'international' : 'india';
    selectRegion(newKey);
  }, [regionKey, selectRegion]);

  const openRegionPicker = useCallback(() => {
    setShowRegionPicker(true);
  }, []);

  const closeRegionPicker = useCallback(() => {
    if (regionKey) setShowRegionPicker(false);
  }, [regionKey]);

  const formatPrice = useCallback((priceInINR) => {
    if (currency === 'USD') {
      const usd = Math.round(priceInINR / exchangeRate / 10) * 10 || Math.round(priceInINR / exchangeRate);
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(usd);
    }
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(priceInINR);
  }, [currency, exchangeRate]);

  const rawConvert = useCallback((priceInINR) => {
    return currency === 'USD' ? Math.round((priceInINR / exchangeRate) * 100) / 100 : priceInINR;
  }, [currency, exchangeRate]);

  return (
    <RegionContext.Provider value={{
      region,
      regionKey: regionKey || 'india',
      regions: REGIONS,
      currency,
      exchangeRate,
      selectRegion,
      toggleRegion,
      openRegionPicker,
      closeRegionPicker,
      showRegionPicker,
      formatPrice,
      rawConvert,
      isIndia: regionKey === 'india',
      isInternational: regionKey === 'international',
    }}>
      {children}
    </RegionContext.Provider>
  );
};

export const useRegion = () => useContext(RegionContext);

// Backward compatibility
export const useCurrency = () => {
  const ctx = useRegion();
  return {
    currency: ctx.currency,
    toggleCurrency: ctx.toggleRegion,
    formatPrice: ctx.formatPrice,
    rawConvert: ctx.rawConvert,
    exchangeRate: ctx.exchangeRate,
  };
};
